import React, { useState, useRef, useEffect } from "react";
import { round, parseNumber } from "../shared/helpers";
import "../style.css";

export default function ElectricalCalculator() {
  const [systemKw, setSystemKw] = useState("");
  const [inverterVoltage, setInverterVoltage] = useState(240);
  const [panelVmp, setPanelVmp] = useState(38);
  const [panelIsc, setPanelIsc] = useState(9.5);
  const [panelsPerString, setPanelsPerString] = useState(12);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const handleCalculate = () => {
    setError("");
    const sizeKw = parseNumber(systemKw);
    const vmp = parseNumber(panelVmp);
    const isc = parseNumber(panelIsc);
    const stringCount = Math.max(1, Math.floor(parseNumber(panelsPerString, 1)));

    if (sizeKw <= 0) {
      setError("Enter a valid system size in kW to calculate electrical design.");
      return;
    }
    if (vmp <= 0 || isc <= 0 || inverterVoltage <= 0 || stringCount <= 0) {
      setError("All inputs must be positive numbers to compute a safe string design.");
      return;
    }
    const sizeKwFixed = Math.max(0, sizeKw);
    const dcPower = sizeKwFixed * 1000;
    const arrayVoltage = round(stringCount * vmp, 1);
    const dcStringCurrent = round(dcPower / arrayVoltage, 2);
    const acCurrent = round(dcPower / inverterVoltage, 2);
    const inverterDerate = 0.96;
    const arrayCurrentWithDerate = round(dcStringCurrent / inverterDerate, 2);
    const breakerSize = round(acCurrent * 1.25, 1);
    const combinerFuses = Math.max(1, Math.ceil((dcStringCurrent * 1.25) / 15));

    setResult({
      arrayVoltage,
      dcStringCurrent,
      arrayCurrentWithDerate,
      acCurrent,
      breakerSize,
      combinerFuses,
    });
  };

  return (
    <div className="se-wrap">
      {/* <div className="se-hero">
        <div className="se-hero-badge">Electrical Calculator</div>
        <h1 className="se-hero-h1">Solar electrical design</h1>
        <p className="se-hero-p">Estimate current, voltage, breaker sizing, and combiner recommendations for US rooftop solar.</p>
      </div> */}

      <div className="se-card">
        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">System size (kW)</label>
            <input className="se-input" type="number" min="0.1" step="0.1" value={systemKw} onChange={e => setSystemKw(e.target.value)} placeholder="e.g. 8" />
          </div>
          <div className="se-field">
            <label className="se-label">Inverter voltage (V)</label>
            <input className="se-input" type="number" min="1" step="1" value={inverterVoltage} onChange={e => setInverterVoltage(e.target.value)} />
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">Panel Vmp (V)</label>
            <input className="se-input" type="number" min="1" step="0.1" value={panelVmp} onChange={e => setPanelVmp(e.target.value)} />
          </div>
          <div className="se-field">
            <label className="se-label">Panel Isc (A)</label>
            <input className="se-input" type="number" min="0.1" step="0.1" value={panelIsc} onChange={e => setPanelIsc(e.target.value)} />
          </div>
        </div>

        <div className="se-field">
          <label className="se-label">Modules per string</label>
          <input className="se-input" type="number" value={panelsPerString} min="1" step="1" onChange={e => setPanelsPerString(e.target.value)} />
        </div>
        {error && <div className="se-err">{error}</div>}

        <div className="se-nav-row">
          <span />
          <button className="se-btn-next" onClick={handleCalculate}>Calculate electricals</button>
        </div>
      </div>

      {result && (
        <div ref={resultRef} className="se-card">
          <div className="se-results-grid">
            <div className="se-res-card"><div className="se-res-label">Array voltage</div><div className="se-res-val">{result.arrayVoltage} V</div></div>
            <div className="se-res-card"><div className="se-res-label">DC string current</div><div className="se-res-val">{result.dcStringCurrent} A</div></div>
            <div className="se-res-card"><div className="se-res-label">AC current</div><div className="se-res-val">{result.acCurrent} A</div></div>
            <div className="se-res-card"><div className="se-res-label">Breaker size</div><div className="se-res-val">{result.breakerSize} A</div></div>
          </div>
          <div className="se-results-grid">
            <div className="se-res-card"><div className="se-res-label">Derated current</div><div className="se-res-val">{result.arrayCurrentWithDerate} A</div></div>
            <div className="se-res-card"><div className="se-res-label">Combiner fuses</div><div className="se-res-val">{result.combinerFuses}</div></div>
          </div>
          <div className="se-disclaimer">
            Estimates based on US EIA 2024 data, NREL PVWatts methodology, and the 30% federal ITC.<br />
            Actual results may vary. Consult a certified solar installer for a site-specific quote.
          </div>
        </div>
      )}
    </div>
  );
}
